import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/client';

const ROLES = [
  { value: 'dj', label: 'DJ' },
  { value: 'editor', label: 'Editor' },
  { value: 'operator', label: 'Operator' },
  { value: 'advertising', label: 'Advertising' },
  { value: 'admin', label: 'Admin' },
];

export default function UserForm({ editMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = editMode && Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'dj',
    is_active: true,
    avatar_url: '',
  });

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      fetchUser();
    }
  }, [id]);

  const fetchUser = async () => {
    try {
      const { data } = await api.get(`/api/users/${id}`);
      setFormData({
        name: data.name || '',
        email: data.email || '',
        role: data.role || 'dj',
        is_active: data.is_active ?? true,
        avatar_url: data.avatar_url || '',
        password: '',
      });
    } catch (err) {
      console.error('[UserForm] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    if (!isEdit && !formData.password) {
      newErrors.password = 'Password is required for new users';
    }
    if (!formData.role) newErrors.role = 'Role is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = { ...formData };
      if (!isEdit && !payload.password) delete payload.password;

      if (isEdit) {
        await api.put(`/api/users/${id}`, payload);
      } else {
        await api.post('/api/users', payload);
      }
      navigate('/users');
    } catch (err) {
      console.error('[UserForm] Save failed:', err);
      if (err.response?.data?.message) {
        setErrors({ submit: err.response.data.message });
      } else {
        setErrors({ submit: 'Failed to save user.' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading user...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">
          {isEdit ? 'Edit User' : 'Create User'}
        </h1>
        <Link to="/users" className="btn btn-secondary btn-sm">
          Back to Users
        </Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Avatar */}
        <div className="flex flex-col items-center gap-3 rounded-xl bg-dark-850 border border-dark-700 p-6">
          <img
            src={
              formData.avatar_url ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.name || 'User')}&background=7c3aed&color=fff`
            }
            alt="avatar"
            className="h-24 w-24 rounded-full object-cover"
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.name || 'User')}&background=7c3aed&color=fff`;
            }}
          />
          <input
            type="text"
            name="avatar_url"
            value={formData.avatar_url}
            onChange={handleChange}
            className="input text-sm text-center"
            placeholder="Avatar URL"
          />
        </div>

        {/* Form fields */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input ${errors.name ? 'border-error-500' : ''}`}
                placeholder="Full name"
              />
              {errors.name && <p className="text-xs text-error-400 mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`input ${errors.email ? 'border-error-500' : ''}`}
                placeholder="email@example.com"
              />
              {errors.email && <p className="text-xs text-error-400 mt-1">{errors.email}</p>}
            </div>

            {!isEdit && (
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`input ${errors.password ? 'border-error-500' : ''}`}
                  placeholder="••••••••"
                />
                {errors.password && <p className="text-xs text-error-400 mt-1">{errors.password}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Role *</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className={`input ${errors.role ? 'border-error-500' : ''}`}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {errors.role && <p className="text-xs text-error-400 mt-1">{errors.role}</p>}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
            />
            <label htmlFor="is_active" className="text-sm text-dark-300">Active</label>
          </div>
        </div>
      </form>

      <div className="flex justify-end gap-3 border-t border-dark-700 pt-6">
        <Link to="/users" className="btn btn-secondary">
          Cancel
        </Link>
        <button type="submit" disabled={saving} className="btn btn-primary">
          {saving ? 'Saving...' : isEdit ? 'Update User' : 'Create User'}
        </button>
      </div>
    </div>
  );
}
